/**
 * B621 — the tenant's clock facts, carried by context (the
 * VocabularyProvider pattern): an app mounts ClockProvider with the
 * tenant's stored timezone + timeFormat (ops.config's facts, however
 * they reach that app — the admin's one tenant-facts read, the
 * storefront's mint), and shared components read them through
 * useClock(). No provider answers the empty default — the ruled 12h
 * face and the caller's own zone fallback stay the reader's.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { TimeFormat } from "./clock.js";

export interface ClockFacts {
  readonly timeZone?: string;
  readonly timeFormat?: TimeFormat;
}

const ClockContext = createContext<ClockFacts>({});

export interface ClockProviderProps {
  readonly clock: ClockFacts;
  readonly children: ReactNode;
}

/** Mounted once per app, fed from the tenant's stored clock facts. */
export function ClockProvider({ clock, children }: ClockProviderProps) {
  return <ClockContext.Provider value={clock}>{children}</ClockContext.Provider>;
}

/** The only door from a shared component to the tenant's clock. */
export function useClock(): ClockFacts {
  return useContext(ClockContext);
}
